import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { User } from 'src/app/shared/models/user.model';
import { UserService } from '../../services/user.service';
import { EditUserDataComponent } from '../edit-user-data/edit-user-data.component';
import Swal from 'sweetalert2';
import { SetupMfaComponent } from './setup-mfa/setup-mfa.component';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.sass']
})
export class UserProfileComponent implements OnInit, OnDestroy {

  userData: User | undefined;
  subscription: Subscription = new Subscription();
  userSession: string = '';
  MFAauthStatus: boolean = false;
  userRole: string = '';
  constructor( private _snackBar: MatSnackBar, private userService: UserService, private setupMFaDialog: MatDialog, private cognito: CognitoService, private userProfileDialog: MatDialog) {
    this.initUserForm();
    this.getPreferredMFA();
    this.getUserRole();
  }
  ngOnDestroy(){
    this.subscription.unsubscribe();
  }

  get canDisableMfa(): boolean {
    return this.userRole === 'aguacate_client';
  }

  async getUserRole(): Promise<void> {
    try {
      const user = await this.cognito.getUser();
      this.userRole = user?.attributes?.['custom:user-group'] || '';
    } catch (error) {
      this.userRole = '';
    }
  }

  toggleMfa(){
    if(this.MFAauthStatus){
      this.confirmEnableMfa();
    } else {
      this.confirmDisableMfa();
    }
  }

  private confirmEnableMfa(): void {
    Swal.fire({
      title: '¿Estás seguro?',
      html: `
        <p style="margin: 0 0 12px 0;">Estás a punto de habilitar la autenticación multifactor.</p>
        <p style="margin: 0 0 12px 0;">Esta validación es <strong>requerida</strong> para proteger la información de nuestros clientes y verificar la autenticidad de cada registro.</p>
        <p style="margin: 0 0 12px 0;">Una vez activada, deberás <strong>ingresar el código del MFA cada vez que inicies sesión</strong>.</p>
        <p style="margin: 0;">Te sugerimos utilizar la aplicación <strong>Google Authenticator</strong>.</p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1e5a3f',
      confirmButtonText: 'Sí, habilitar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const isMobileMfa = window.innerWidth < 768;
          const dialogConfig = new MatDialogConfig();

          dialogConfig.disableClose = false;
          dialogConfig.autoFocus = true;
          dialogConfig.maxHeight = '90vh';
          dialogConfig.maxWidth = isMobileMfa ? '95vw' : '500px';
          dialogConfig.minWidth = isMobileMfa ? '90vw' : '460px';
          dialogConfig.data = this.userData;

          const dialogRef = this.setupMFaDialog.open(SetupMfaComponent, dialogConfig);
          dialogRef.afterClosed().subscribe((mfaActivated) => {
            // Siempre refresca el status preferido — el operador puede
            // haber cerrado el modal con MFA ya activado o cancelado.
            this.getPreferredMFA();
            // Si el setup terminó exitosamente, también refrescamos la
            // data del usuario para que la UI refleje cualquier cambio
            // relacionado (atributos Cognito, flags en backend, etc.).
            if (mfaActivated === true) {
              this.fecthUserData();
            }
          });
        } catch (error) {}
      } else {
        this.MFAauthStatus = true;
      }
    });
  }

  private confirmDisableMfa(): void {
    if (!this.canDisableMfa) {
      Swal.fire({
        icon: 'warning',
        title: 'Acción no permitida',
        text: 'Tu rol no tiene permitido desactivar la autenticación multifactor.',
        confirmButtonColor: '#1e5a3f',
      });
      this.MFAauthStatus = true;
      return;
    }

    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Estás a punto de desactivar la autenticación multifactor. Esto reduce la seguridad de tu cuenta.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#1e5a3f',
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (!result.isConfirmed) {
        this.MFAauthStatus = true;
        return;
      }
      try {
        await this.cognito.disableMFA();
        await this.getPreferredMFA();
        Swal.fire({
          icon: 'success',
          title: 'MFA desactivado',
          text: 'La autenticación multifactor fue desactivada correctamente.',
          confirmButtonColor: '#1e5a3f',
        });
      } catch (error) {
        await this.getPreferredMFA();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo desactivar la autenticación multifactor.',
          confirmButtonColor: '#1e5a3f',
        });
      }
    });
  }

  async getPreferredMFA(){
    const mfaStatus = await this.cognito.getMFAStatus();
    switch(mfaStatus){
      case 'NOMFA':
        this.MFAauthStatus  = false;
        break;
      default:
        this.MFAauthStatus = true;
        break;
    }
  }

  openDialog(){
    const isMobile = window.innerWidth < 768;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.maxHeight = '90vh';
    dialogConfig.maxWidth = isMobile ? '95vw' : '826px';
    dialogConfig.minWidth = isMobile ? '90vw' : '800px';
    dialogConfig.data = this.userData;

    this.userProfileDialog.open(EditUserDataComponent, dialogConfig).afterClosed().subscribe(res=>{
      if(res === true){
        this._snackBar.open('Datos actualizados', 'Ok');
        this.fecthUserData();
        return;
      }
      else if(res === false){
        this._snackBar.open('Hubo un error al actualizar los datos', 'Ok');
      }

    });
  }
  async initUserForm(){
    const userInSession = await this.cognito.getUser();
    this.userSession = userInSession.attributes['email'];
    this.fecthUserData();
  }
  fecthUserData(){
    this.subscription.add(this.userService.getUserData(this.userSession).subscribe(({user}: any)=>{
      this.userData = user;
    }));
  }
  ngOnInit() {
  }

}
